import React, { useCallback, useEffect, useState, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  Bug,
  CreditCard,
  HelpCircle,
  Lightbulb,
  Lock,
  Mail,
  X,
  ArrowLeft,
  UploadCloud,
  Image as ImageIcon,
  XCircle,
} from "lucide-react";
import { createSupportTicket } from "../../services/v2/supportTicketService";
import { getLocalStorage } from "../../helpers/common";
import { USER_INFO_KEY } from "../../constants/common";
import HelpChatPanel from "./HelpChatPanel";
import "./SupportTicketModal.scss";
import { useCopilotEnabled } from "@/hooks/useCopilotEnabled";
import {
  defaultTicketModalMode,
  shouldShowChatPanel,
  shouldShowTicketForm,
} from "../../helpers/supportTicketMode";

const CATEGORIES = [
  {
    id: "BUG",
    label: "Bug Report",
    Icon: Bug,
    helper: "Something isn't working as expected",
  },
  {
    id: "BILLING",
    label: "Billing",
    Icon: CreditCard,
    helper: "Payments, invoices, or plan changes",
  },
  {
    id: "QUESTION",
    label: "Question",
    Icon: HelpCircle,
    helper: "How does something work?",
  },
  {
    id: "FEATURE_REQUEST",
    label: "Feature Request",
    Icon: Lightbulb,
    helper: "Suggest an improvement",
  },
  {
    id: "ACCOUNT",
    label: "Account Help",
    Icon: Lock,
    helper: "Login, profile, or access issues",
  },
  { id: "OTHER", label: "Other", Icon: Mail, helper: "Anything else" },
];

// Attachments are emailed inline (base64 → CID), so limits are kept email-safe.
const MAX_FILES = 3;
const MAX_FILE_BYTES = 4 * 1024 * 1024;
const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,image/heic";

const MIN_SUBJECT = 3;
const MIN_DESCRIPTION = 10;

// Read a File into the base64 string the backend expects (data-URL prefix stripped).
const fileToAttachment = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result || "").split(",")[1] || "";
      resolve({
        filename: file.name,
        mimeType: file.type,
        contentBase64: base64,
      });
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });

const SupportTicketModal = ({ show, onHide, initialTranscript, initialTicket }) => {
  const userInfo = getLocalStorage(USER_INFO_KEY)?.userInfo || {};

  // The Copilot is a BLS agent feature. On Gitcha this modal is a support
  // ticket form and nothing else - it used to open on the chat tab for every
  // product, which put the agent assistant in front of Gitcha consumers.
  const copilotEnabled = useCopilotEnabled();
  const [mode, setMode] = useState(() =>
    defaultTicketModalMode(copilotEnabled),
  );
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState(null);
  const [firstName, setFirstName] = useState(userInfo.firstName || "");
  const [lastName, setLastName] = useState(userInfo.lastName || "");
  const [email, setEmail] = useState(userInfo.email || "");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setMode(defaultTicketModalMode(copilotEnabled));
    setStep(1);
    setCategory(null);
    setSubject("");
    setDescription("");
    setFiles([]);
    setSubmitting(false);
  };

  // Pipe the chat transcript into the ticket description so the user doesn't
  // have to re-explain themselves. Jumps straight to step 2 (skip the
  // category picker) with a reasonable default category - the user is
  // already mid-conversation, re-picking a category is friction, not signal.
  const escalateFromChat = useCallback((transcript) => {
    setMode("ticket");
    setCategory(CATEGORIES.find((c) => c.id === "QUESTION"));
    setStep(2);
    setDescription(
      `From chat:\n${transcript}\n\nWhat I still need help with:\n`,
    );
  }, []);

  const handleClose = () => {
    if (submitting) return;
    reset();
    onHide && onHide();
  };

  // Lets a caller (the floating chat bubble) open this modal already jumped
  // to the ticket flow with a transcript prefilled, instead of duplicating
  // the ticket form's category/upload/validation logic in a second place.
  useEffect(() => {
    if (show && initialTranscript) {
      escalateFromChat(initialTranscript);
    }
  }, [show, initialTranscript, escalateFromChat]);

  const ticketSubject = initialTicket?.subject || "";
  const ticketDescription = initialTicket?.description || "";
  useEffect(() => {
    if (!show || !(ticketSubject || ticketDescription)) return;
    setMode("ticket");
    setCategory(CATEGORIES.find((c) => c.id === "QUESTION"));
    setStep(2);
    setSubject(ticketSubject);
    setDescription(ticketDescription);
  }, [show, ticketSubject, ticketDescription]);

  const subjectLen = subject.trim().length;
  const descLen = description.trim().length;
  const subjectTooShort = subjectLen > 0 && subjectLen < MIN_SUBJECT;
  const descTooShort = descLen > 0 && descLen < MIN_DESCRIPTION;

  const validStep2 = useMemo(() => {
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!/^\S+@\S+\.\S+$/.test(email)) return false;
    if (subject.trim().length < MIN_SUBJECT) return false;
    if (description.trim().length < MIN_DESCRIPTION) return false;
    return true;
  }, [firstName, lastName, email, subject, description]);

  const ingestFiles = (incoming) => {
    const merged = [...files];
    for (const f of incoming) {
      if (merged.length >= MAX_FILES) {
        toast.warning(`Max ${MAX_FILES} attachments`);
        break;
      }
      if (!f.type || !f.type.startsWith("image/")) {
        toast.warning(`${f.name} is not an image`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        toast.warning(`${f.name} exceeds 4 MB`);
        continue;
      }
      merged.push(f);
    }
    setFiles(merged);
  };

  const onPickFiles = (e) => {
    ingestFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    ingestFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      let attachments = [];
      if (files.length > 0) {
        attachments = await Promise.all(files.map(fileToAttachment));
      }
      await createSupportTicket({
        category: category.id,
        priority: category.id === "BUG" ? "HIGH" : "NORMAL",
        submitterName: `${firstName.trim()} ${lastName.trim()}`,
        submitterEmail: email.trim(),
        subject: subject.trim(),
        description: description.trim(),
        // Origin + path only, never the full href - a query string in this
        // app's URL scheme can carry a sensitive value (a token, an email),
        // and a support ticket is emailed inline, so leaking one into
        // window.location.href here would be the same class of leak
        // HelpChatPanel's own `currentPage` field deliberately avoids by
        // sending window.location.pathname alone (see HelpChatPanel.js -
        // not touched here, matching its policy rather than duplicating its
        // reasoning).
        sourceUrl: window.location.origin + window.location.pathname,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        attachments,
      });
      toast.success("We got your request - check your email for confirmation");
      reset();
      onHide && onHide();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not submit ticket. Try again.",
      );
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop={submitting ? "static" : true}
      keyboard={!submitting}
      dialogClassName="support-ticket-modal"
    >
      <Modal.Body>
        <div className="support-ticket-header">
          <div>
            <h3>How can we help you?</h3>
          </div>
          <button
            type="button"
            className="close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        {copilotEnabled && (
          <div className="mode-tabs">
            <button
              type="button"
              className={`mode-tab${mode === "chat" ? " active" : ""}`}
              onClick={() => !submitting && setMode("chat")}
            >
              Genie
            </button>
            <button
              type="button"
              className={`mode-tab${mode === "ticket" ? " active" : ""}`}
              onClick={() => !submitting && setMode("ticket")}
            >
              Send a ticket
            </button>
          </div>
        )}

        {shouldShowChatPanel(copilotEnabled, mode) && (
          <HelpChatPanel onEscalateToTicket={escalateFromChat} />
        )}

        {shouldShowTicketForm(copilotEnabled, mode) && step === 1 && (
          <div className="support-step support-step-category">
            <p className="step-helper" style={{ marginBottom: 16 }}>
              Pick a category so we can route your message to the right person.
            </p>
            <div className="category-grid">
              {CATEGORIES.map((c) => {
                const IconComp = c.Icon;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`category-card${category?.id === c.id ? " selected" : ""}`}
                    onClick={() => {
                      setCategory(c);
                      setStep(2);
                    }}
                  >
                    <span className="category-icon-wrap">
                      <IconComp size={22} strokeWidth={1.6} />
                    </span>
                    <span className="category-label">{c.label}</span>
                    <span className="category-helper">{c.helper}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {shouldShowTicketForm(copilotEnabled, mode) && step === 2 && (
          <div className="support-step support-step-details">
            <div className="step-tag-row">
              <button
                type="button"
                className="back-btn"
                onClick={() => setStep(1)}
                disabled={submitting}
                aria-label="Back"
              >
                <ArrowLeft size={16} strokeWidth={2} />
              </button>
              <div className="step-tag">
                {category.Icon && <category.Icon size={14} strokeWidth={1.8} />}
                <span>{category.label}</span>
              </div>
            </div>
            <div className="row-2">
              <div className="field">
                <label htmlFor="support-first-name">First name</label>
                <input
                  id="support-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Sarah"
                />
              </div>
              <div className="field">
                <label htmlFor="support-last-name">Last name</label>
                <input
                  id="support-last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Mitchell"
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="support-email">Email</label>
              <input
                id="support-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div className="field">
              <label htmlFor="support-subject">Subject</label>
              <input
                id="support-subject"
                type="text"
                placeholder="Short summary"
                maxLength={120}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                aria-describedby="support-subject-hint"
                aria-invalid={subjectTooShort}
              />
              <div className="field-footer">
                <span
                  id="support-subject-hint"
                  className={`field-hint${subjectTooShort ? " field-hint--warn" : ""}`}
                >
                  {subjectTooShort ? `At least ${MIN_SUBJECT} characters` : ""}
                </span>
              </div>
            </div>
            <div className="field">
              <label htmlFor="support-description">
                Describe what's happening
              </label>
              <textarea
                id="support-description"
                placeholder="What were you trying to do? What did you expect? What happened instead?"
                rows={5}
                maxLength={4000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                aria-describedby="support-description-hint"
                aria-invalid={descTooShort}
              />
              <div className="field-footer">
                <span
                  id="support-description-hint"
                  className={`field-hint${descTooShort ? " field-hint--warn" : ""}`}
                >
                  {descLen >= MIN_DESCRIPTION
                    ? ""
                    : descLen > 0
                      ? `${MIN_DESCRIPTION - descLen} more character${
                          MIN_DESCRIPTION - descLen === 1 ? "" : "s"
                        } needed`
                      : `Minimum ${MIN_DESCRIPTION} characters`}
                </span>
                <span className="char-counter">{description.length}/4000</span>
              </div>
            </div>
            <div className="field attachments-field">
              <label>
                Screenshots{" "}
                <span className="label-meta">
                  optional · up to {MAX_FILES} images
                </span>
              </label>
              <label
                className={`drop-zone${dragOver ? " over" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!dragOver) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
              >
                <input
                  type="file"
                  multiple
                  accept={ACCEPTED_TYPES}
                  onChange={onPickFiles}
                />
                <UploadCloud size={22} strokeWidth={1.6} />
                <div className="drop-zone-text">
                  <strong>Drag images here</strong>
                  <span>
                    or click to browse · PNG, JPG, WebP, HEIC · max 4 MB each
                  </span>
                </div>
              </label>
              {files.length > 0 && (
                <ul className="file-list">
                  {files.map((f, idx) => (
                    <li key={idx}>
                      <span className="file-icon">
                        <ImageIcon size={16} strokeWidth={1.8} />
                      </span>
                      <span className="file-name" title={f.name}>
                        {f.name}
                      </span>
                      <span className="file-size">
                        {(f.size / 1024).toFixed(0)} KB
                      </span>
                      <button
                        type="button"
                        className="file-remove"
                        onClick={() => removeFile(idx)}
                        aria-label={`Remove ${f.name}`}
                      >
                        <XCircle size={16} strokeWidth={1.8} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="step-actions">
              <Button
                variant="primary"
                className="primary-cta"
                disabled={!validStep2 || submitting}
                onClick={submit}
              >
                {submitting ? "Sending..." : "Send to support"}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SupportTicketModal;
